/**
 * offlineQueue — Queue workouts for offline submission, sync when back online.
 * Stores pending workouts in AsyncStorage and retries on connectivity restore.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { AppState, Platform } from 'react-native';
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { insertStrengthEvents } from '@/lib/strengthEvents';
import { enqueueScoreJob } from '@/lib/scoreQueue';
import { autoSubmitChallenges } from '@/lib/challengeAutoSubmit';
import { evaluateBadges } from '@/lib/badges';
import { setSyncStatus } from './offlineStatus';

const log = createLogger('OFFLINE_QUEUE');
const QUEUE_KEY = 'duel:offline_workout_queue';

export interface QueuedWorkout {
  id: string;
  userId: string;
  gymId: string | null;
  performedAt: string;
  payload: any;
  queuedAt: string;
  retryCount: number;
}

class OfflineQueue {
  private syncing = false;
  private initialized = false;
  private appStateSubscription: any = null;

  async init() {
    if (this.initialized) return;
    this.initialized = true;

    // Listen for connectivity changes (NetInfo optional)
    if (Platform.OS !== 'web') {
      try {
        const NetInfo = require('@react-native-community/netinfo').default;
        NetInfo.addEventListener((state: any) => {
          if (state.isConnected) {
            log.info('Network restored, syncing offline queue');
            this.syncAll();
          }
        });
      } catch {
        // NetInfo not installed — skip connectivity listener
      }
    }

    // Sync on app foreground
    const appStateSubscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        this.syncAll();
      }
    });
    // Store for cleanup
    this.appStateSubscription = appStateSubscription;

    // Initial sync attempt
    this.syncAll();
  }

  async enqueue(workout: Omit<QueuedWorkout, 'id' | 'queuedAt' | 'retryCount'>): Promise<void> {
    const queue = await this.getQueue();
    const entry: QueuedWorkout = {
      ...workout,
      id: `offline-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      queuedAt: new Date().toISOString(),
      retryCount: 0,
    };
    queue.push(entry);
    await this.saveQueue(queue);
    log.info('Workout queued offline', { id: entry.id });
  }

  async syncAll(): Promise<{ synced: number; failed: number }> {
    if (this.syncing) return { synced: 0, failed: 0 };
    this.syncing = true;

    let synced = 0;
    let failed = 0;

    try {
      const queue = await this.getQueue();
      if (queue.length === 0) return { synced: 0, failed: 0 };

      log.info(`Syncing ${queue.length} offline workouts`);
      setSyncStatus('syncing', queue.length);
      const remaining: QueuedWorkout[] = [];

      for (const workout of queue) {
        try {
          const { data: insertedWorkout, error } = await supabase.from('workouts').insert({
            user_id: workout.userId,
            gym_id: workout.gymId,
            performed_at: workout.performedAt,
            payload: workout.payload,
          }).select().single();

          if (error) {
            workout.retryCount++;
            if (workout.retryCount < 5) {
              remaining.push(workout);
            }
            failed++;
            log.warn('Failed to sync workout', { id: workout.id, error: error.message, retries: workout.retryCount });
          } else {
            synced++;
            log.info('Synced offline workout', { id: workout.id });

            // Run post-save pipeline
            try {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('id, units, bodyweight, home_gym_id, gym_id')
                .eq('id', workout.userId)
                .single();

              if (profileData && insertedWorkout) {
                const performedAt = insertedWorkout.performed_at || workout.performedAt;
                const workoutPayload = typeof workout.payload === 'string'
                  ? JSON.parse(workout.payload)
                  : workout.payload;

                try {
                  await insertStrengthEvents(profileData, workoutPayload, performedAt, insertedWorkout.id);
                } catch (e) {
                  log.warn('Post-sync insertStrengthEvents failed', { id: workout.id, error: (e as any)?.message });
                }

                try {
                  await autoSubmitChallenges(
                    { id: profileData.id, gym_id: profileData.gym_id },
                    workout.userId,
                  );
                } catch (e) {
                  log.warn('Post-sync autoSubmitChallenges failed', { id: workout.id, error: (e as any)?.message });
                }

                try {
                  await enqueueScoreJob(workout.userId, 'workout_save');
                } catch (e) {
                  log.warn('Post-sync enqueueScoreJob failed', { id: workout.id, error: (e as any)?.message });
                }

                try {
                  await evaluateBadges(workout.userId);
                } catch (e) {
                  log.warn('Post-sync evaluateBadges failed', { id: workout.id, error: (e as any)?.message });
                }
              }
            } catch (pipelineErr) {
              log.warn('Post-sync pipeline error', { id: workout.id, error: (pipelineErr as any)?.message });
            }
          }
        } catch (e) {
          workout.retryCount++;
          if (workout.retryCount < 5) {
            remaining.push(workout);
          } else {
            // Permanently dropped — surface to user via stored notification
            log.error('Workout permanently dropped after 5 retries', { workoutId: workout.id });
            try {
              const { saveDroppedWorkoutAlert } = await import('@/lib/notifications');
              await saveDroppedWorkoutAlert(workout);
            } catch { /* non-blocking */ }
          }
          failed++;
        }
      }

      await this.saveQueue(remaining);
      setSyncStatus('synced', remaining.length);
      if (synced > 0) log.info(`Synced ${synced} workouts, ${failed} failed, ${remaining.length} remaining`);
    } finally {
      this.syncing = false;
    }

    return { synced, failed };
  }

  async getPendingCount(): Promise<number> {
    const queue = await this.getQueue();
    return queue.length;
  }

  async clearAll(): Promise<void> {
    await AsyncStorage.removeItem(QUEUE_KEY);
  }

  private async getQueue(): Promise<QueuedWorkout[]> {
    try {
      const raw = await AsyncStorage.getItem(QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private async saveQueue(queue: QueuedWorkout[]): Promise<void> {
    await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }

  destroy() {
    this.appStateSubscription?.remove();
  }
}

export const offlineQueue = new OfflineQueue();
